import {
    Body1,
    Button,
    Subtitle1,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components'
import { ErrorCircleRegular } from '@fluentui/react-icons'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import commonStyles, { useAppStyles } from '../styles/common.styles'

const useStyles = makeStyles({
  root: {
    ...commonStyles.flexColumn24,
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
    textAlign: 'center',
    minHeight: '240px',
    height: '100%',
  },
  icon: {
    ...commonStyles.flexRow2,
    justifyContent: 'center',
    height: '64px',
    width: '64px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorPaletteRedForeground1,
  },
})

interface ErrorFallbackProps {
  message: string | undefined
  onRetry: () => void
  className?: string
}

export function ErrorFallback({ message, onRetry, className }: ErrorFallbackProps) {
  const styles = useStyles()
  const appStyles = useAppStyles()

  return (
    <div className={mergeClasses(styles.root, className)}>
      <div className={styles.icon}>
        <ErrorCircleRegular fontSize={48} />
      </div>
      <div className={appStyles.flexColumn8}>
        <Subtitle1 align="center">Something went wrong</Subtitle1>
        <Body1 align="center" style={{ color: tokens.colorNeutralForeground2, maxWidth: 480 }}>
          {message || 'An unexpected error occurred.'}
        </Body1>
      </div>
      <Button appearance="primary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

interface Props {
  children: ReactNode
  fallback?: ReactNode
  className?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <ErrorFallback
          message={this.state.error?.message}
          onRetry={() => this.setState({ hasError: false, error: null })}
          className={this.props.className}
        />
      )
    }

    return this.props.children
  }
}
